import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Card, Overline, Metric, Empty, Pill, Spinner } from "@/components/Widgets";
import { PageGuide } from "@/components/PageGuide";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { Users, CurrencyDollar, ChartBar, Package, Robot, Play, Megaphone, Article as ArticleIcon } from "@phosphor-icons/react";

export default function Admin() {
  const [tab, setTab] = useState("accounting");
  const [acc, setAcc] = useState(null);
  const [subs, setSubs] = useState(null);
  const [plans, setPlans] = useState(null);
  const [ap, setAp] = useState(null);
  const [apBusy, setApBusy] = useState(false);
  const [reb, setReb] = useState(null);

  const loadAp = () => api.get("/admin/autopilot").then(({ data }) => setAp(data)).catch(() => {});
  useEffect(() => {
    api.get("/admin/accounting").then(({ data }) => setAcc(data)).catch(() => {});
    api.get("/admin/subscribers").then(({ data }) => setSubs(data)).catch(() => {});
    api.get("/admin/plans").then(({ data }) => setPlans(data)).catch(() => {});
    api.get("/admin/rebuild-articles").then(({ data }) => setReb(data)).catch(() => {});
    loadAp();
  }, []);

  const saveAp = async (enabled, interval) => {
    try {
      await api.put("/admin/autopilot", { enabled, interval_minutes: interval });
      loadAp();
      toast.success(enabled ? "Auto-Pilot ON" : "Auto-Pilot paused");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || err.message); }
  };
  const runAp = async () => {
    setApBusy(true);
    try {
      const { data } = await api.post("/admin/autopilot/run");
      toast.success(`Published: ${data.title}`);
      loadAp();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || err.message); }
    finally { setApBusy(false); }
  };
  const genMarketing = async () => {
    setApBusy(true);
    try {
      const { data } = await api.post("/marketing/generate");
      toast.success(`Marketing article published: ${data.title}`, { action: { label: "View", onClick: () => window.open("/blog", "_blank") } });
      loadAp();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || err.message); }
    finally { setApBusy(false); }
  };

  return (
    <div className="space-y-6 fade-up">
      <PageGuide title="How the Admin Console works" testid="admin-guide" steps={[
        "Accounting: track MRR, revenue, active subscribers and total web articles published.",
        "Subscribers: view every user, their plan, status, article count and lifetime value.",
        "Plans: see how many subscribers are on each pricing tier.",
        "Auto-Pilot: turn on 24/7 auto-publishing, set how often it runs, or publish now to promote the platform.",
      ]} />
      <div className="flex gap-1 border-b border-border">
        {[["accounting", "Accounting", CurrencyDollar], ["subscribers", "Subscribers", Users], ["rebuilds", "Rebuild Articles", ArticleIcon], ["plans", "Plans", Package], ["autopilot", "Auto-Pilot", Robot]].map(([id, label, Icon]) => (
          <button key={id} data-testid={`admin-tab-${id}`} onClick={() => setTab(id)}
            className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors duration-200 ${
              tab === id ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-white"
            }`}>
            <Icon size={16} weight="bold" /> {label}
          </button>
        ))}
      </div>

      {tab === "accounting" && (
        acc ? (
          <div className="space-y-6" data-testid="accounting-panel">
            <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
              <Metric label="MRR" value={`$${acc.mrr}`} sub="monthly recurring" testid="metric-mrr" />
              <Metric label="Total Revenue" value={`$${acc.total_revenue}`} sub="paid to date" testid="metric-revenue" />
              <Metric label="Active Subscribers" value={acc.active_subscribers} sub={`of ${acc.total_users} users`} testid="metric-active-subs" />
              <Metric label="Transactions" value={acc.transactions_count} sub="successful" testid="metric-txn-count" />
              <Metric label="Web Articles" value={acc.articles_published ?? 0} sub="published SEO" testid="metric-articles-published" />
            </div>

            <Card testid="revenue-by-plan">
              <Overline className="mb-3">REVENUE BY PLAN</Overline>
              {acc.by_plan?.some((p) => p.revenue > 0) ? (
                <div style={{ height: 200 }}>
                  <ResponsiveContainer>
                    <BarChart data={acc.by_plan}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#1c1c21" />
                      <XAxis dataKey="plan" tick={{ fill: "#6b7280", fontSize: 11 }} />
                      <YAxis tick={{ fill: "#6b7280", fontSize: 11 }} />
                      <Tooltip contentStyle={{ background: "#0f0f0f", border: "1px solid #262626", fontSize: 12 }} cursor={{ fill: "#ffffff08" }} />
                      <Bar dataKey="revenue" fill="#E9AC0C" radius={[2, 2, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <Empty>No paid revenue yet. Revenue appears once subscribers check out.</Empty>
              )}
            </Card>

            <Card testid="recent-transactions">
              <Overline className="mb-3">RECENT TRANSACTIONS</Overline>
              {acc.recent_transactions?.length ? (
                <div className="divide-y divide-border">
                  {acc.recent_transactions.map((t) => (
                    <div key={t.session_id} className="py-2.5 flex items-center justify-between gap-3">
                      <div className="min-w-0">
                        <div className="text-sm truncate">{t.user_email || "—"}</div>
                        <div className="overline text-muted-foreground">{t.lookup_key} · {(t.created_at || "").slice(0, 10)}</div>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="font-mono text-sm">${t.amount}</span>
                        <Pill tone={t.payment_status === "paid" ? "positive" : t.payment_status === "pending" ? "medium" : "negative"}>{t.payment_status}</Pill>
                      </div>
                    </div>
                  ))}
                </div>
              ) : <Empty>No transactions yet.</Empty>}
            </Card>
          </div>
        ) : <Spinner label="Loading accounting…" />
      )}

      {tab === "subscribers" && (
        subs ? (
          <Card testid="subscribers-panel">
            <Overline className="mb-3">ALL SUBSCRIBERS · {subs.length}</Overline>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left overline text-muted-foreground border-b border-border">
                    <th className="py-2 pr-4">Name</th><th className="py-2 pr-4">Email</th>
                    <th className="py-2 pr-4">Role</th><th className="py-2 pr-4">Plan</th>
                    <th className="py-2 pr-4">Status</th><th className="py-2 pr-4">Articles</th><th className="py-2 pr-4">LTV</th>
                  </tr>
                </thead>
                <tbody>
                  {subs.map((s) => (
                    <tr key={s.id} data-testid={`subscriber-${s.id}`} className="border-b border-border/50">
                      <td className="py-2.5 pr-4">{s.name}</td>
                      <td className="py-2.5 pr-4 text-muted-foreground">{s.email}</td>
                      <td className="py-2.5 pr-4">{s.role === "admin" ? <Pill tone="high">admin</Pill> : <span className="text-muted-foreground">user</span>}</td>
                      <td className="py-2.5 pr-4">{s.plan || <span className="text-muted-foreground">—</span>}</td>
                      <td className="py-2.5 pr-4"><Pill tone={s.subscription_status === "active" ? "positive" : "neutral"}>{s.subscription_status}</Pill></td>
                      <td className="py-2.5 pr-4 font-mono">{s.articles_published ?? 0}</td>
                      <td className="py-2.5 pr-4 font-mono">${s.lifetime_value}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        ) : <Spinner label="Loading subscribers…" />
      )}

      {tab === "rebuilds" && (
        reb ? (
          <div className="space-y-6" data-testid="rebuilds-panel">
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
              <Metric label="Rebuild Articles Live" value={reb.total_articles ?? 0} sub="published to the web" testid="metric-rebuild-articles" />
              <Metric label="Subscribers Publishing" value={reb.subscribers?.length ?? 0} sub="with live rebuilds" testid="metric-rebuild-subs" />
            </div>
            {reb.subscribers?.length === 0 ? (
              <Empty>No reputation-rebuild articles published yet. They appear here as subscribers publish to the web.</Empty>
            ) : (
              reb.subscribers.map((g) => (
                <Card key={g.user_id} testid={`rebuild-sub-${g.user_id}`}>
                  <div className="flex items-center gap-2 mb-3">
                    <h3 className="font-head font-bold text-lg">{g.name}</h3>
                    <span className="text-sm text-muted-foreground">{g.email}</span>
                    <span className="font-mono text-sm text-primary ml-auto">{g.articles.length} articles</span>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-3">
                    {g.articles.map((a) => (
                      <a key={a.id} href={`/blog/${a.slug}`} target="_blank" rel="noreferrer" data-testid={`admin-rebuild-article-${a.id}`}
                        className="border border-border p-3 hover:border-primary transition-colors duration-200 group">
                        <div className="text-sm font-medium group-hover:text-primary transition-colors duration-200 line-clamp-2">{a.title}</div>
                        <div className="overline text-muted-foreground mt-2 flex items-center gap-2">
                          <span className="text-emerald-400">● LIVE</span>
                          <span>{(a.published_at || "").slice(0, 10)}</span>
                          <span className="ml-auto">{a.views || 0} views</span>
                        </div>
                      </a>
                    ))}
                  </div>
                </Card>
              ))
            )}
          </div>
        ) : <Spinner label="Loading rebuild articles…" />
      )}

      {tab === "plans" && (
        plans ? (
          <div className="grid md:grid-cols-3 gap-4" data-testid="plans-panel">
            {plans.map((p) => (
              <Card key={p.id} testid={`admin-plan-${p.id}`}>
                <div className="flex items-center justify-between">
                  <h3 className="font-head font-bold text-lg">{p.name}</h3>
                  <span className="font-mono font-bold">${p.price}/mo</span>
                </div>
                <div className="mt-3 flex items-baseline gap-2">
                  <span className="font-mono text-3xl font-bold text-primary">{p.active_subscribers}</span>
                  <span className="text-sm text-muted-foreground">active subscribers</span>
                </div>
                <div className="mt-3 text-xs text-muted-foreground font-mono">key: {p.id}</div>
              </Card>
            ))}
          </div>
        ) : <Spinner label="Loading plans…" />
      )}

      {tab === "autopilot" && (
        ap ? (
          <div className="space-y-6" data-testid="autopilot-panel">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <Metric label="Status" value={ap.enabled ? "ON" : "Paused"} sub="24/7 publishing" testid="ap-status" />
              <Metric label="Interval" value={`${ap.interval_minutes}m`} sub="between runs" testid="ap-interval" />
              <Metric label="Total Runs" value={ap.runs ?? 0} sub="auto publishes" testid="ap-runs" />
              <Metric label="Marketing Articles" value={ap.marketing_articles ?? 0} sub="promoting SMI" testid="ap-marketing" />
            </div>
            <Card testid="autopilot-controls">
              <Overline className="mb-4">AUTO-PILOT CONTROLS</Overline>
              <div className="flex flex-wrap items-center gap-4">
                <button onClick={() => saveAp(!ap.enabled, ap.interval_minutes)} data-testid="ap-toggle-btn"
                  className={`px-4 py-2.5 text-sm font-semibold transition-colors duration-200 ${ap.enabled ? "border border-border hover:text-destructive" : "bg-primary text-primary-foreground font-semibold hover:bg-primary/90"}`}>
                  {ap.enabled ? "Pause Auto-Pilot" : "Turn Auto-Pilot ON"}
                </button>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-muted-foreground">Run every</span>
                  <input type="number" min="1" max="1440" defaultValue={ap.interval_minutes} data-testid="ap-interval-input"
                    onBlur={(e) => saveAp(ap.enabled, Math.max(1, Number(e.target.value)))}
                    className="w-20 bg-secondary border border-border px-2 py-1.5 text-sm focus:outline-none focus:border-primary" />
                  <span className="text-sm text-muted-foreground">minutes</span>
                </div>
                <button onClick={runAp} disabled={apBusy} data-testid="ap-run-btn"
                  className="ml-auto bg-primary text-primary-foreground font-semibold px-4 py-2.5 text-sm font-semibold flex items-center gap-2 hover:bg-primary/90 transition-colors duration-200 disabled:opacity-50">
                  {apBusy ? <Spinner label="Publishing…" /> : (<><Play size={16} weight="fill" /> Publish now</>)}
                </button>
                <button onClick={genMarketing} disabled={apBusy} data-testid="ap-marketing-btn"
                  className="border border-border px-4 py-2.5 text-sm font-semibold flex items-center gap-2 hover:border-primary hover:text-primary transition-colors duration-200 disabled:opacity-50">
                  <Megaphone size={16} weight="bold" /> Publish marketing article
                </button>
              </div>
              <p className="text-xs text-muted-foreground mt-4">
                When ON, the engine auto-publishes queued subscriber articles first, then promotional articles marketing the platform — keeping fresh positive content flowing to the web {ap.pending_campaign_articles} queued.
              </p>
            </Card>
          </div>
        ) : <Spinner label="Loading auto-pilot…" />
      )}
    </div>
  );
}
